<template>
  <div class="aftersale-image-material">
    <el-container class="material-body" :style="{ minHeight: bodyMinHeight + 'px' }">
      <el-aside class="material-aside" width="250px" v-loading="treeLoading">
        <div class="aside-head">文件夹</div>
        <div class="tree-wrap">
          <el-tree
            v-if="treeData.length"
            ref="folderTree"
            class="folder-tree"
            :data="treeData"
            :props="treeProps"
            node-key="id"
            highlight-current
            default-expand-all
            :expand-on-click-node="true"
            @node-click="handleTreeNodeClick"
          />
          <div v-else-if="!treeLoading" class="tree-empty">暂无目录数据</div>
        </div>
      </el-aside>

      <el-main class="material-main">
        <el-tabs v-model="activeTab" type="border-card" class="material-tabs">
          <el-tab-pane label="列表" name="list" :lazy="false">
            <div v-if="!selectedTreeNode" class="right-tip">
              <span class="right-tip__text">请从左侧选择目录，列表数据由素材接口加载</span>
            </div>
            <div class="table-wrap">
              <el-table
                :data="displayRows"
                style="width: 100%"
                :height="tableHeight"
                border
                v-loading="listLoading"
                element-loading-text="数据加载中"
              >
                <el-table-column prop="id" label="id" width="80" />
                <el-table-column prop="type_name" label="类型" width="90" align="center" />
                <el-table-column prop="material_code" label="素材编码" min-width="160" show-overflow-tooltip />
                <el-table-column prop="material_name" label="素材名称" min-width="140" show-overflow-tooltip />
                <el-table-column prop="desc" label="描述" min-width="140" show-overflow-tooltip />
                <el-table-column label="封面图" width="200" align="center">
                  <template slot-scope="scope">
                    <el-image
                      v-if="scope.row.cover_img"
                      class="table-content-img"
                      :src="scope.row.cover_img"
                      fit="contain"
                      :preview-src-list="[scope.row.cover_img]"
                    >
                    </el-image>
                  </template>
                </el-table-column>
                <el-table-column label="素材内容" width="200" align="center">
                  <template slot-scope="scope">
                    <el-image
                      v-if="scope.row.type === '1' && scope.row.material_url"
                      class="table-content-img"
                      :src="scope.row.material_url"
                      fit="contain"
                      :preview-src-list="[scope.row.material_url]"
                    >
                      <div slot="error" class="table-img-error">
                        <i class="el-icon-picture-outline" />
                      </div>
                    </el-image>
                    <video
                      v-else-if="scope.row.type === '2' && scope.row.material_url"
                      class="table-content-video"
                      :src="scope.row.material_url"
                      controls
                      controlslist="nodownload"
                    >
                      您的浏览器不支持 video 标签。
                    </video>
                    <span v-else class="table-cell-empty">—</span>
                  </template>
                </el-table-column>
                <el-table-column prop="material_version" label="素材版本" width="100" align="center" />
                <el-table-column prop="status_name" label="状态" width="100" align="center" />
              </el-table>
              <div class="content-padded content-center table-pagination">
                <el-pagination
                  v-if="selectedTreeNode"
                  layout="total, prev, pager, next"
                  :current-page.sync="params.page"
                  :total="materialTotal"
                  :page-size="params.pageSize"
                />
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getExternalMaterialFolderTree,
  getAftersaleImageMaterialList
} from '@/api/aftersale_video'

/** 左侧树根节点 id（与接口真实 id 区分） */
const TREE_ROOT_ID = '__ALL_DIRECTORIES__'

function parseJsonField(val) {
  if (val == null) return null
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return null
    }
  }
  return val
}

function unwrapResponseData(res) {
  if (!res || res.data === undefined) return null
  let inner = res.data
  while (inner && typeof inner === 'object' && !Array.isArray(inner) && inner.data !== undefined) {
    inner = inner.data
  }
  return inner
}

function isRootNode(node) {
  return !node || node._isRootAll || String(node.id) === String(TREE_ROOT_ID)
}

function extractTreePayload(res) {
  const inner = unwrapResponseData(res)
  if (!inner) return []
  if (Array.isArray(inner)) return inner
  if (typeof inner !== 'object') return []

  let tree = inner.tree !== undefined ? inner.tree : inner.list
  if (typeof tree === 'string') {
    const parsed = parseJsonField(tree)
    tree = Array.isArray(parsed) ? parsed : []
  }

  if (Array.isArray(tree)) return tree
  if (Array.isArray(inner.list)) return inner.list
  return []
}

function normalizeTreeNodes(nodes) {
  if (!Array.isArray(nodes)) return []
  return nodes.map((node) => {
    const n = { ...node }
    let children = n.children
    if (children == null) {
      n.children = []
    } else if (typeof children === 'string') {
      const parsed = parseJsonField(children)
      n.children = Array.isArray(parsed) ? normalizeTreeNodes(parsed) : []
    } else if (Array.isArray(children)) {
      n.children = normalizeTreeNodes(children)
    } else {
      n.children = []
    }
    return n
  })
}

/** 从素材列表接口中提取 list + total_count */
function extractMaterialListPayload(res) {
  const inner = unwrapResponseData(res)
  if (!inner) return { list: [], total: 0 }

  if (Array.isArray(inner)) {
    return { list: inner, total: inner.length }
  }
  if (inner && typeof inner === 'object') {
    if (Array.isArray(inner.list)) {
      const total = Number(inner.total_count != null ? inner.total_count : inner.list.length) || 0
      return { list: inner.list, total }
    }
    if (Array.isArray(inner.rows)) {
      const total = Number(inner.total != null ? inner.total : inner.rows.length) || 0
      return { list: inner.rows, total }
    }
  }
  return { list: [], total: 0 }
}

function mapMaterialRowToTable(row) {
  if (!row || typeof row !== 'object') return {}
  const rawType = row.type != null ? String(row.type) : (row.material_type != null ? String(row.material_type) : '')
  return {
    id: row.id,
    type: rawType,
    type_name: rawType === '1' ? '图片' : rawType === '2' ? '视频' : '-',
    material_code: row.material_code != null ? row.material_code : row.code != null ? row.code : '',
    material_name: row.material_name || row.name || '',
    desc: row.desc != null ? row.desc : row.description != null ? row.description : '',
    material_url: row.material_url || row.url || '',
    material_version: row.material_version != null ? row.material_version : '',
    status_name: row.status_name || row.status || '',
    cover_img: row.cover_img || row.cover_url || ''
  }
}

export default {
  name: 'AftersaleImageMaterial',
  data() {
    return {
      /** 接口返回的目录树（不含「全部目录」根） */
      folderList: [],
      selectedTreeNode: null,
      /** 右侧列表（新接口） */
      materialList: [],
      materialTotal: 0,
      listLoading: false,
      params: {
        page: 1,
        pageSize: 10
      },
      treeLoading: false,
      activeTab: 'list',
      treeProps: {
        children: 'children',
        label: 'name'
      }
    }
  },
  computed: {
    ...mapGetters(['wheight']),
    bodyMinHeight() {
      return Math.max(420, (this.wheight || 600) - 220)
    },
    /** 最外层手动增加「全部目录」，其 children 为接口 tree */
    treeData() {
      const list = normalizeTreeNodes(this.folderList)
      return [
        {
          id: TREE_ROOT_ID,
          name: '全部目录',
          full_path: '',
          children: list,
          _isRootAll: true
        }
      ]
    },
    hasSelectedFolder() {
      return !isRootNode(this.selectedTreeNode)
    },
    tableHeight() {
      return Math.max(320, (this.wheight || 600) - 280)
    },
    materialTableRows() {
      return (this.materialList || []).map(mapMaterialRowToTable)
    },
    displayRows() {
      const { page, pageSize } = this.params
      const start = (page - 1) * pageSize
      return this.materialTableRows.slice(start, start + pageSize)
    }
  },
  methods: {
    resetPage() {
      this.params.page = 1
    },
    resetMaterialList() {
      this.materialList = []
      this.materialTotal = 0
    },
    buildMaterialListQuery() {
      const node = this.selectedTreeNode
      if (isRootNode(node)) return {}
      return { folder_id: node.id }
    },
    handleTreeNodeClick(data) {
      this.selectedTreeNode = data
      this.resetPage()

      if (isRootNode(data)) {
        this.resetMaterialList()
        return
      }

      this.fetchMaterialList()
    },
    /**
     * 右侧列表：对接 getAftersaleImageMaterialList。
     * 接口未就绪时请求会失败，列表为空（控制台会提示）。
     */
    async fetchMaterialList() {
      if (!this.hasSelectedFolder) {
        this.resetMaterialList()
        return
      }

      this.listLoading = true
      try {
        const res = await getAftersaleImageMaterialList(this.buildMaterialListQuery())
        const payload = extractMaterialListPayload(res)
        this.materialList = Array.isArray(payload.list) ? payload.list : []
        this.materialTotal = Number(payload.total) || this.materialList.length
      } catch (err) {
        console.warn('[aftersale image material] 素材列表接口未就绪或请求失败', err)
        this.resetMaterialList()
      } finally {
        this.listLoading = false
      }
    },
    async fetchFolderTree() {
      this.treeLoading = true
      try {
        const res = await getExternalMaterialFolderTree({ tree: 1, parent_id: 0 })
        this.folderList = normalizeTreeNodes(extractTreePayload(res))
        if (!this.folderList.length) {
          console.warn('[aftersale image material] tree 为空，请检查接口返回结构', res && res.data)
        }
        this.$nextTick(() => {
          this.selectDefaultTreeNode()
        })
      } catch (err) {
        console.error('[aftersale image material] external-folder/tree 请求失败:', err)
        this.folderList = []
      } finally {
        this.treeLoading = false
      }
    },
    selectDefaultTreeNode() {
      const root = this.treeData[0]
      if (!root || !this.$refs.folderTree) return
      this.$refs.folderTree.setCurrentKey(root.id)
      this.selectedTreeNode = root
      this.resetMaterialList()
    }
  },
  mounted() {
    this.fetchFolderTree()
  }
}
</script>

<style scoped lang="scss">
.aftersale-image-material {
  padding: 0 4px 16px;
}

.material-body {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
}

.material-aside {
  border-right: 1px solid #ebeef5;
  padding: 0;
  background: #fafafa;
  min-height: 200px;
}

.aside-head {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
}

.tree-wrap {
  padding: 8px 0;
  background: #fafafa;
  min-height: 120px;
}

.folder-tree {
  background: transparent;

  ::v-deep .el-tree-node__content {
    height: 36px;
    font-size: 13px;
  }

  ::v-deep .el-tree-node.is-current > .el-tree-node__content {
    background-color: #ecf5ff;
    color: #409eff;
  }
}

.tree-empty {
  padding: 16px;
  color: #909399;
  font-size: 13px;
}

.material-main {
  padding: 0;
  background: #fff;
}

.right-tip {
  padding: 8px 0 12px;
}

.right-tip__text {
  font-size: 13px;
  color: #909399;
}

.table-wrap {
  min-height: 200px;
}

.material-tabs {
  border: none;
  box-shadow: none;

  ::v-deep .el-tabs__header {
    margin: 0;
    border-bottom: 1px solid #ebeef5;
  }

  ::v-deep .el-tabs__content {
    padding: 12px 16px 8px;
  }
}

.table-content-img {
  width: 160px;
  height: 90px;
  border-radius: 4px;
  background: #f5f7fa;
}

.table-content-video {
  display: block;
  width: 160px;
  height: 90px;
  border-radius: 4px;
  background: #f5f7fa;
}

.table-img-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 90px;
  font-size: 28px;
  color: #c0c4cc;
}

.table-cell-empty {
  color: #c0c4cc;
  font-size: 13px;
}

.table-pagination {
  margin-top: 12px;
  padding-bottom: 4px;
}
</style>
